import Vue from 'vue';
import moment from 'moment';
import api from '../../api';
import csv from '../../utils/csv';

const state = {
  projects: {},
  all_projects: {},
  all_project_ids: [],
  project_schema: {
    createdById: null,
    organizationId: null,
    categoryId: null,
    name: '',
    description: null,
    startDate: null,
    dueDate: null,
    effort: null,
    status: 'Active',
    private: false,
    currencyId: 1,
    procurementContact: null,
    estAnnualSpend: null,
    signedOffDate: null,
    type: 'Tender',
    rfpReleaseDate: null,
    rfpResponsesDueDate: null,
    anticipatedAwardDate: null,
    negPlanCompleted: null,
    anticipatedRenewalDate: null,
    anticipatedNoticeDate: null,
    anticipatedExitDate: null,
    benefitsNotes: null,
    projectMembers: [],
    projectBenefits: [],
    projectSuppliers: [],
    projectFiles: [],
    relatedProjectsIds: [],
  },
  benefit_schema: {
    organizationId: null,
    yearEnding: null,
    forecastedAmount: null,
    forecastedDate: null,
    amount: null,
    realizedDate: null,
    spendType: null,
    type: null,
    savingsTypeId: null,
    confidence: null,
    state: 'Forecasted',
    // approvals: []
  }
};

const getters = {
  projects(state) {
    return state.projects;
  },
  all_projects(state) {
    return state.all_projects;
  },
  all_project_ids(state) {
    return state.all_project_ids;
  },
  project_schema(state) {
    return state.project_schema;
  },
  benefit_schema(state) {
    return state.benefit_schema;
  }
};

const mutations = {
  setProjects(state, payload) {
    state.projects = payload;
  },
  setAllProjects(state, payload) {
    state.all_projects = payload;
  },
  setAllProjectIds(state, payload) {
    state.all_project_ids = payload;
  },
  async setProject(state, payload) {
    if (payload && payload.shortId) {
      // TODO: This causes the project to briefly not exist
      await Vue.delete(state.projects, payload.shortId);
      await Vue.set(state.projects, payload.shortId, payload);
      await Vue.set(state.all_projects, payload.shortId, payload);
    }
  },
};

const actions = {
  async getAllProjects(context) {
    return new Promise(async (resolve, reject) => {
      try {
        const params = [
          { key: 'limit', value: 100000 },
          { key: 'offset', value: 0 },
          { key: 'orderBy', value: 'name' },
          { key: 'order', value: 'ASC' },
        ];

        const { processed_projects } = await context.dispatch(
          'getProjectsData',
          params
        );
        if (processed_projects) {
          context.commit('setAllProjects', processed_projects);
        }

        const ids = Object.keys(processed_projects).map(
          key => processed_projects[key].id
        );
        context.commit('setAllProjectIds', ids);

        resolve();
      } catch (e) {
        console.error(e);
        reject(e);
      }
    });
  },
  async getProjectsData(context, params) {
    return new Promise(async (resolve, reject) => {
      try {
        // If the user is a Stakeholder user we only want to hit the Self-Service projects endpoint
        // This gets called by ProjectModal so we can't avoid it in Self-Service mode
        // TODO separate this better
        const projects = context.getters.app_user.role === 'Stakeholder' ?
          // Hit Self-Service endpoint for projects
          await api.getSelfServiceProjects()
          // Hit traditional projects endpoint for the projects
          : await api.getProjects(params); // Hit Self-Service endpoint for projects

        if (
          projects &&
          projects.data &&
          projects.data.projects &&
          projects.data.projects.items
        ) {
          const processed_projects = {};

          projects.data.projects.items.forEach(project => {
            project = processProject(project, context);

            // assign project to object
            const org = context.getters.org;
            if (org && org.id && project.organizationId === org.id) {
              processed_projects[project.shortId] = project;
            }
          });

          resolve({ projects, processed_projects });
        }
      } catch (e) {
        console.error(e);
        reject(e);
      }
    });
  },
  async getProjects(context, params) {
    return new Promise(async (resolve, reject) => {
      try {
        const res = await context.dispatch('getProjectsData', params);
        context.commit('setProjects', res.processed_projects);
        resolve(res.projects.data.projects);
      } catch (e) {
        console.error(e);
        reject(e);
      }
    });
  },
  async getProjectsCSV(context, params) {
    return new Promise(async (resolve, reject) => {
      try {
        const res = await context.dispatch('getProjectsData', params);
        const {csv_fields, data} = prepCSVfields(
          res.projects.data.projects.items,
          context.rootGetters
        );
        const csv_file = csv.generate(
          csv_fields,
          data
        );
        resolve(csv_file);
      } catch (e) {
        console.error(e);
        reject(e);
      }
    });
  },
  async createBenefitApproval(context, params){
    const {
      benefitId,
      savingsTypeId,
      confidence,
      yearEnding,
      forecastedAmount,
      forecastedDate,
      realizedAmount,
      realizedDate
    } = params;

    return new Promise((resolve, reject) => {
      api.requestBenefitApproval({
        benefitId,
        savingsTypeId,
        confidence,
        yearEnding,
        forecastedAmount,
        forecastedDate,
        realizedAmount,
        realizedDate
      })
        .then(({data}) => {
          const benefitApproval = data.projectBenefitApproval;
          const benefit = Object.values(context.getters.projects).reduce((arr, proj) => {
            return arr.concat(proj.projectBenefits || [])
          }, []).find((benefit) => benefit.id === benefitApproval.benefitId);

          if (benefit) {
            if(!Array.isArray(benefit.approvals)) Vue.set(benefit, 'approvals', [])
            else benefit.approvals.splice(0);
            benefit.approvals[0] = benefitApproval;
          }

          resolve(benefitApproval);
        })
        .catch(error => {
          throw error
        });
    })
  },
  async cancelBenefitApproval(context, approvalId){
    return new Promise((resolve, reject) => {
      api.cancelBenefitApprovalRequest({id: approvalId})
        .then(({data}) => {
          const benefitApproval = data.projectBenefitApproval;
          const benefit = Object.values(context.getters.projects).reduce((arr, proj) => {
            return arr.concat(proj.projectBenefits || [])
          }, []).find((benefit) => benefit.id === benefitApproval.benefitId);

          if (benefit) {
            const oldApproval = benefit.approvals.find(el => el.id === benefitApproval.id);
            if(oldApproval) {
              const index = benefit.approvals.indexOf(oldApproval);
              Vue.set(benefit.approvals, index, Object.assign(oldApproval, {...benefitApproval}));
            };
          }

          resolve(benefitApproval);
        })
        .catch(error => {
          throw error
        });
    })
  },
  getProjectDetail(context, id) {
    return new Promise(async (resolve, reject) => {
      try {
        let new_project = await api.getProjectDetail(id);
        new_project = processProject(new_project.data.project, context);

        context.commit('setProject', new_project);
        resolve(new_project);
      } catch (e) {
        reject(e);
      }
    });
  },
  saveProject(context, project_to_clone) {
    return new Promise(async (resolve, reject) => {
      try {
        // we need a reference to the original project, so we can compare which suppliers have changed etc
        const original_project =
          context.getters.projects[project_to_clone.shortId];

        // we need to clone the project object, so if saving fails we haven't mutated the original object
        let project = JSON.stringify(project_to_clone);
        project = JSON.parse(project);
        project.estAnnualSpend = Number(project.estAnnualSpend);
        sanitiseDates(project);
        prepareSuppliersForSave(project, original_project);
        prepareBenefitsForSave(project, original_project);
        prepareMembersForSave(project, original_project);
        prepareFilesForSave(project, original_project);
        makeDatesISO(project);
        removeNulls(project);

        const type = project.id && project.id !== -1 ? 'update' : 'create';
        const new_project = await api.saveProject(project, type);

        let id = project.id;

        if (
          new_project &&
          new_project.data &&
          new_project.data.createdProject
        ) {
          id = new_project.data.createdProject.id;
        }

        if (id) {
          await context.dispatch('getProjectDetail', id);
          resolve(id);
        } else {
          throw new Error('project id not set');
        }

        if (new_project && new_project.data) {
          const {data} = new_project;
          if (data.rulesApplied) {
            context.dispatch('getProjectActions', {
              id,
              request_time: moment(),
            })
          }
        }

        if (type === 'create') {
          context.dispatch('getAllProjects');
        }
      } catch (e) {
        console.error(e);
        reject(e);
      }
    });
  },
  setProject(context, payload){
    if (!payload.shortId) return
    context.commit('setProject', payload);
  },
  deleteProject(context, id) {
    return new Promise(async (resolve, reject) => {
      try {
        await api.deleteProject(id);
        resolve();
      } catch (e) {
        console.error(e);
        reject(e);
      }
    });
  },
  createSelfServiceProject(context, payload){
    return new Promise(async (resolve, reject) => {
      try{
        if (Array.isArray(payload.responses)) {
          for (const response of payload.responses) {
            if (response.node) {
              const {id, label, nodeType, nextNode} = response.node;
              response.node = {id, label, nodeType, nextNode};
            }
          }
        }
        const response = await api.createSelfServiceProject(payload)

        let id = payload.id;

        if (
          response &&
          response.data &&
          response.data.createdProject
        ) {
          id = response.data.createdProject.id;
        }

        const { data } = response;

        if (id) {
          resolve(data);
        } else {
          throw new Error('Project id not set');
        }
      } catch (e) {
        console.error(e)
        reject(e)
      }
    })
  }
};

const processProject = (project, context) => {
  processProjectBenefits(project, context);
  processProjectMembers(project, context);
  processProjectSuppliers(project, context);
  processProjectDocuments(project, context);
  if (project.relatedProjects) {
    project.relatedProjects = project.relatedProjects.sort((a, b) => {
      if (a.name.toLowerCase() < b.name.toLowerCase()) {
        return -1;
      }
      return 1;
    });

    project.relatedProjectsIds = project.relatedProjects.map(
      r => r.RelatedProjects.relatedProjectId
    );
  }

  return project;
};

const processProjectBenefits = (project, context) => {
  const orgId = context.getters.org.id;
  const benefit_schema = context.getters.benefit_schema;

  project.total_benefits = 0;
  project.fy_benefits = 0;
  project.forecasted_benefits = 0;
  project.forecasted_fy_benefits = 0;

  benefit_schema.organizationId = orgId;

  const thisYear = new Date().getFullYear();
  const fy_month = context.getters.app_user.organization.fy_month;

  let current_fy_end = moment(fy_month + ' ' + thisYear, 'MMMM YYYY').subtract(
    1,
    'd'
  );
  if (moment().isAfter(current_fy_end)) {
    current_fy_end = current_fy_end.add(1, 'y');
  }

  project.projectBenefits = project.projectBenefits || [benefit_schema];
  project.projectBenefits.forEach(benefit => {
    benefit.amount = Number(benefit.amount);
    project.total_benefits += benefit.amount;
    benefit.forecastedAmount = Number(benefit.forecastedAmount);
    project.forecasted_benefits += benefit.forecastedAmount;

    const benefit_year = moment(benefit.yearEnding, 'YYYY');
    const is_current_year = current_fy_end.isSame(benefit_year, 'y');

    if (is_current_year) {
      project.fy_benefits += benefit.amount;
      project.forecasted_fy_benefits += benefit.forecastedAmount;
    }
  });
};

const processProjectMembers = (project, context) => {
  const users = context.getters.users;

  project.owners = [];
  project.sponsors = [];
  project.members = [];
  project.others = [];
  project.projectMembers = project.projectMembers || [];

  project.projectMembers.forEach((member, index) => {
    const member_details = users[member.userId];

    if (member_details) {
      const type = member.type;
      member = Object.assign(member, member_details);
      member.type = type;

      if (member.type === 'Owner') {
        project.owners.push(member);
      } else if (member.type === 'Sponsor') {
        project.sponsors.push(member);
      } else if (member.type === 'Team') {
        project.members.push(member);
      } else if (member.type === 'Other') {
        project.others.push(member);
      }
    } else {
      project.projectMembers[index] = null;
    }
  });

  project.projectMembers = project.projectMembers.filter(member => !!member);
};

const processProjectSuppliers = (project, context) => {
  const suppliers = context.getters.suppliers;

  project.projectSuppliers = project.projectSuppliers || [];

  const getInitials = name => {
    if (typeof name !== 'string') {
      return '';
    }

    const nameArray = name.split(' ');
    return nameArray
      .map(n => n.charAt(0))
      .slice(0, 4)
      .join('')
      .toUpperCase();
  };

  project.projectSuppliers.forEach((supplier, index) => {
    const supplier_details = suppliers[supplier.supplierId];

    if (supplier_details) {
      if (!supplier.name) {
        // project.projectSuppliers[index] = Object.assign(supplier, supplier_details)
        supplier.name = supplier_details.name;
        supplier.meta = supplier_details.meta;
        supplier.initials = getInitials(supplier_details.name);
        supplier.supplierId = supplier_details.id;
        supplier.internalCode = supplier_details.internalCode;
        supplier.registerNumber = supplier_details.registerNumber;
        supplier.address = supplier_details.address;
        supplier.verified = supplier_details.verified;
      }
    } else {
      project.projectSuppliers[index] = null;
    }
  });

  project.projectSuppliers = project.projectSuppliers.filter(
    supplier => !!supplier
  );
};

const processProjectDocuments = (project, context) => {
  if (!project.projectDocuments) {
    project.projectDocuments = [];
  }

  project.projectDocuments = project.projectDocuments.sort((a, b) => {
    if (a.title.toLowerCase() < b.title.toLowerCase()) {
      return -1;
    }
    return 1;
  });
};

const sanitiseDates = project => {
  Object.keys(project).forEach(key => {
    const date = isValidDate(project[key]);
    if (date.isDate && !date.isValidDate) {
      project[key] = null;
    }
  });
};

const isValidDate = d => {
  return {
    isDate: d instanceof Date,
    isValidDate: d instanceof Date && !isNaN(d),
  };
};

const makeDatesISO = project => {
  Object.keys(project).forEach(key => {
    if (key.toLowerCase().includes('date') || key === 'negPlanCompleted') {
      project[key] = moment(project[key]).toISOString();
    }
  });
};

const prepareSuppliersForSave = (project, original_project) => {
  if (project && Array.isArray(project.projectSuppliers)) {
    const projectSuppliers = (project.projectSuppliers || []);
    const projectSupplierIds = projectSuppliers.map(e => e.supplierId);

    const originalSuppliers = ((original_project && original_project.projectSuppliers) || []).reduce((obj, supplier) => {
      obj[supplier.supplierId] = supplier;
      return obj
    }, {});

    let newSuppliers = [];
    let updatedSuppliers = [];
    let deletedSuppliers = Object.values(originalSuppliers)
      .filter(s => !projectSupplierIds.includes(s.supplierId))
      .map(s => ({
        id: s.id,
        supplierId: s.supplierId,
        willDeleted: true
      }))

    projectSuppliers.forEach(supplier => {
      if(!originalSuppliers[supplier.supplierId]){
        const {type, supplierId, cost, ratings, comment, contactName, contactEmail} = supplier;
        newSuppliers.push({type, supplierId, cost, ratings, comment, contactName, contactEmail});
        return;
      }

      // Map out all relevant fields from the current project supplier
      const currentObject = (
        ({ type, cost, ratings, comment, contactName, contactEmail, linkedFiles }) =>
          ({ type, cost, ratings, comment, contactName, contactEmail, linkedFiles })
      )(supplier);
      currentObject.linkedFiles =
        currentObject.linkedFiles ? currentObject.linkedFiles.map(file => file.id) : [];

      // Map out all relevant fields from the original project supplier
      const originalObject = (
        ({ type, cost, ratings, comment, contactName, contactEmail, linkedFiles }) =>
          ({ type, cost, ratings, comment, contactName, contactEmail, linkedFiles })
      )(originalSuppliers[supplier.supplierId]);
      originalObject.linkedFiles =
        originalObject.linkedFiles ? originalObject.linkedFiles.map(file => file.id) : [];

      if(JSON.stringify(currentObject) !== JSON.stringify(originalObject)){
        const {id, supplierId, type, cost, ratings, comment, contactName, contactEmail, linkedFiles} = supplier;
        updatedSuppliers.push({id, supplierId, type, cost, ratings, comment, contactName, contactEmail, linkedFiles: linkedFiles ? linkedFiles.map(file => file.id) : []});
      }
    })

    project.projectSuppliers = [...newSuppliers, ...updatedSuppliers, ...deletedSuppliers];
  }
};

const prepareBenefitsForSave = (project, original_project) => {
  if (project && project.projectBenefits) {
    let new_benefits = [];
    let deleted_benefits = [];

    const has_original_benefits =
      original_project && original_project.projectBenefits;
    const original_benefits = has_original_benefits
      ? original_project.projectBenefits
      : [];
    const original_keys = has_original_benefits
      ? original_project.projectBenefits.map(b => b.id)
      : [];

    const updated_benefits = project.projectBenefits || [];
    const updated_keys = project.projectBenefits.map(s => s.id) || [];

    // get an array of any deleted benefits
    if (original_benefits.length) {
      deleted_benefits = original_benefits
        .filter(benefit => {
          if (!updated_keys.includes(benefit.id)) {
            return true;
          }

          let changed = false;
          updated_benefits.forEach(b => {
            const this_changed = benefits_have_changed(benefit, b);
            if (this_changed) {
              changed = true;
            }
          });

          return changed;
        })
        .map(benefit => {
          benefit.willDeleted = true;
          benefit.id = benefit.ProjectToProjectBenefits.projectBenefitsId;
          return benefit;
        });
    }

    // get an array of any new benefits
    new_benefits = updated_benefits
      .filter(benefit => {
        const amount = Number(benefit.amount);
        if (!amount === 0 && !amount) {
          return false;
        }

        if (!original_keys.includes(benefit.id)) {
          return true;
        }

        let changed = false;
        original_benefits.forEach(b => {
          const this_changed = benefits_have_changed(benefit, b);
          if (this_changed) {
            changed = true;
          }
        });
        return changed;
      })
      .map(benefit => {
        Vue.delete(benefit, 'id');
        return benefit;
      });
    project.projectBenefits = [...deleted_benefits, ...new_benefits];
  }
};

const prepareFilesForSave = (project, original_project) => {
  if (project && Array.isArray(project.projectFiles)) {
    const projectFiles = (project.projectFiles || []);
    const projectFileIds = projectFiles.map(e => e.id);

    const originalFiles = ((original_project && original_project.projectFiles) || []).reduce((obj, file) => {
      obj[file.id] = file;
      return obj
    }, {});

    let newFiles = [];
    let updatedFiles = [];
    let deletedFiles = Object.values(originalFiles)
      .filter(f => !projectFileIds.includes(f.id))
      .map(file => ({
        id: file.id,
        fileId: file.id,
        willDeleted: true
      }))

    projectFiles.forEach(file => {
      if(!originalFiles[file.id]){
        const {id} = file;
        newFiles.push({fileId: id});
        return;
      }

      // Map out all relevant fields from the current project file
      const currentObject = (
        ({ id }) =>
          ({ id })
      )(file);

      // Map out all relevant fields from the original project file
      const originalObject = (
        ({ id }) =>
          ({ id })
      )(originalFiles[file.id]);

      if(JSON.stringify(currentObject) !== JSON.stringify(originalObject)){
        const {id} = file;
        updatedFiles.push({id});
      }
    })

    project.projectFiles = [...newFiles, ...updatedFiles, ...deletedFiles];
  }
};

const benefits_have_changed = (benefit, b) => {
  if (b.id === benefit.id) {
    const amount_changed = Number(b.amount) !== Number(benefit.amount);
    const realized_date_changed = b.realizedDate !== benefit.realizedDate;
    const forecasted_amount_changed =
      b.forecastedAmount !== benefit.forecastedAmount;
    const forecasted_date_changed = b.forecastedDate !== benefit.forecastedDate;
    const type_changed = b.type !== benefit.type;
    const year_changed = b.yearEnding !== benefit.yearEnding;
    const spend_type_changed = b.spendType !== benefit.spendType;
    const savings_type_changed = b.savingsTypeId !== benefit.savingsTypeId;
    const confidence_changed = b.confidence !== benefit.confidence;
    const state_changed = b.state !== benefit.state;

    if (
      amount_changed ||
      realized_date_changed ||
      forecasted_amount_changed ||
      forecasted_date_changed ||
      type_changed ||
      year_changed ||
      spend_type_changed ||
      savings_type_changed ||
      confidence_changed ||
      state_changed
    ) {
      return true;
    }
  }

  return false;
};

const prepareMembersForSave = (project, original_project) => {
  if (project && project.projectMembers) {
    let new_members = [];
    let deleted_members = [];

    const has_original_members =
      original_project && original_project.projectMembers;
    const original_members = has_original_members
      ? original_project.projectMembers
      : [];
    const original_keys = has_original_members
      ? original_project.projectMembers.map(m => m.id)
      : [];

    const updated_members = project.projectMembers || [];
    const updated_keys = project.projectMembers.map(m => m.id) || [];

    // get an array of any deleted members
    if (original_members.length) {
      deleted_members = original_members
        .filter(member => {
          if (!updated_keys.includes(member.id)) {
            return true;
          }

          let changed = false;
          updated_members.forEach(m => {
            if (m.id === member.id && m.type !== member.type) {
              changed = true;
            }
          });

          return changed;
        })
        .map(member => {
          return {
            id: member.ProjectToProjectMembers.projectMemberId,
            willDeleted: true,
            type: member.type,
            userId: member.userId,
          };
        });
    }

    // get an array of any new members
    new_members = updated_members
      .filter(member => {
        if (!original_keys.includes(member.id)) {
          return true;
        }

        let changed = false;
        original_members.forEach(m => {
          if (m.id === member.id && m.type !== member.type) {
            changed = true;
          }
        });

        return changed;
      })
      .map(member => {
        return {
          type: member.type,
          userId: member.id,
        };
      });

    project.projectMembers = [...deleted_members, ...new_members];
  }
};

const removeNulls = project => {
  // We shouldn't be removing null date fields!
  // NB! These fields should be marked as nullable on server side schema validation
  const dateFields = ['startDate', 'endDate'];

  Object.keys(project).forEach(key => {
    if (dateFields.includes(key)) return;
    if (project[key] === null) {
      Vue.delete(project, key);
    }
  });
};

const formatDate = date => {
  if (!date) return null;
  return moment(date).format('YYYY-MM-DD');
};

const prepCSVfields = (items, rootGetters) => {
  const confidenceAliases = rootGetters.org.organizationBenefitConfidenceFields.reduce((obj, el) => {
    obj[el.confidence] = el.alias;
    return obj;
  }, {});

  const data = items.reduce((arr, item) => {
    item.ownerNames = item.owners.map(owner => owner.name).join(', ');
    item.sponsorNames = item.sponsors.map(sponsor => sponsor.name).join(', ');
    item.memberNames = item.members.map(member => member.name).join(', ');
    item.otherNames = item.others.map(other => other.name).join(', ');

    item.startDate = formatDate(item.startDate);
    item.dueDate = formatDate(item.dueDate);
    item.rfpReleaseDate = formatDate(item.rfpReleaseDate);
    item.rfpResponsesDueDate = formatDate(item.rfpResponsesDueDate);
    item.negPlanCompleted = formatDate(item.negPlanCompleted);
    item.anticipatedAwardDate = formatDate(item.anticipatedAwardDate);
    item.anticipatedRenewalDate = formatDate(item.anticipatedRenewalDate);
    item.anticipatedNoticeDate = formatDate(item.anticipatedNoticeDate);
    item.anticipatedExitDate = formatDate(item.anticipatedExitDate);

    if(item.categoryId) {
      const rootCategoryId = rootGetters.rootCategory[item.categoryId];
      if (rootCategoryId) {
        item.rootCategory = rootGetters.categoriesFlatMap[rootCategoryId];
        if (rootCategoryId !== item.categoryId) item.subCategory = rootGetters.categoriesFlatMap[item.categoryId];
      }
    }

    if(item.projectBenefits && item.projectBenefits.length) {
      const itemDuplicates = item.projectBenefits.map(projectBenefit => {
        return {
          ...item,
          savingsType: projectBenefit.savingsType ? projectBenefit.savingsType.name : undefined,
          forecastedAmount: projectBenefit.forecastedAmount,
          forecastedDate: formatDate(projectBenefit.forecastedDate),
          amount: projectBenefit.amount,
          realizedDate: formatDate(projectBenefit.realizedDate),
          yearEnding: projectBenefit.yearEnding,
          confidence: confidenceAliases[projectBenefit.confidence] || projectBenefit.confidence
        }
      });
      arr.push(...itemDuplicates);
    } else {
      arr.push(item);
    }
    return arr;
  }, []);

  const csv_fields = [
    {
      label: 'ID',
      value: 'shortId',
    },
    {
      label: 'Name',
      value: 'name',
    },
    {
      label: 'Description',
      value: 'description',
    },
    {
      label: 'Status',
      value: 'status',
    },
    {
      label: 'Team',
      value: 'rootCategory.name',
    },
    {
      label: 'Sub-Category',
      value: 'subCategory.name',
    },
    {
      label: 'Type',
      value: 'type',
    },
    {
      label: 'Project owner(s)',
      value: 'ownerNames',
    },
    {
      label: 'Sponsor(s)',
      value: 'sponsorNames',
    },
    {
      label: 'Key stakeholder(s)',
      value: 'memberNames',
    },
    {
      label: 'Other stakeholder(s)',
      value: 'otherNames',
    },
    {
      label: 'Start date',
      value: 'startDate',
    },
    {
      label: 'Due date',
      value: 'dueDate',
    },
    {
      label: 'Effort (FTE Resource Required)',
      value: 'effort'
    },
    {
      label: 'Currency',
      value: val => rootGetters.currencyCodeById(val.currencyId),
    },
    {
      label: 'Est annual spend',
      value: 'estAnnualSpend',
    },
    {
      label: 'Benefit notes',
      value: 'benefitsNotes',
    },
    {
      label: 'RFP release date',
      value: 'rfpReleaseDate',
    },
    {
      label: 'RFP responses due date',
      value: 'rfpResponsesDueDate',
    },
    {
      label: 'Negotiation plan completed date',
      value: 'negPlanCompleted',
    },
    {
      label: 'Anticipated award date',
      value: 'anticipatedAwardDate',
    },
    {
      label: 'Anticipated renewal date',
      value: 'anticipatedRenewalDate',
    },
    {
      label: 'Anticipated notice date',
      value: 'anticipatedNoticeDate',
    },
    {
      label: 'Anticipated exit date',
      value: 'anticipatedExitDate',
    },
    {
      label: 'Savings Type',
      value: 'savingsType'
    },
    {
      label: 'Forecasted Amount',
      value: 'forecastedAmount'
    },
    {
      label: 'Forecasted Date',
      value: 'forecastedDate'
    },
    {
      label: 'Realised Amount',
      value: 'amount'
    },
    {
      label: 'Realised Date',
      value: 'realizedDate'
    },
    {
      label: 'FY Ending',
      value: 'yearEnding'
    },
    {
      label: rootGetters.org.organizationBenefitConfidenceLabel ?
        rootGetters.org.organizationBenefitConfidenceLabel.alias : 'Confidence',
      value: 'confidence'
    }
  ]

  return {csv_fields, data};
};

export default {
  state,
  getters,
  actions,
  mutations,
};
